import type {ReactElement} from 'react';
import React, {useEffect, useState, useMemo} from 'react';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {CONTROLLED_LIST_ITEM_TYPE, type ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {ControlledList} from '@Components/controlled-list';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {addQrCodeToPage} from '@PosterWhiteboard/items/qr-code-item.class';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import {isTemplatesPopupHidden, onAddBrandAssetClickedHandler, showTemplatesPopupPulsatingDot} from '@Components/poster-editor/library/poster-editor-library';
import {updatePanelProperties, updatePanelSizeData} from '@Components/panel/panel-reducer';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {AddItemPanelLoading} from '@Panels/poster-add-items-panel/components/add-item-panel-loading';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {getNoOfGridItems} from '@Components/base-grid';
import useWindowSize from '@Hooks/useWindowSize';
import {RECORDING_OPTIONS_COOKIE} from '@Components/poster-editor/components/poster-add-item-bar/components/record-dropdown-options/record-dropdown-options.types';
import type {AiOptionsSubpanels} from '@Panels/poster-add-items-panel/components/ai-options/ai-options.types';
import {AI_OPTIONS_HEIGHT} from '@Panels/poster-add-items-panel/components/ai-options/ai-options.types';
import {TEXT_OPTIONS_HEIGHT} from '@Panels/poster-add-items-panel/components/text-options/text-options.type';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateFeatureToShowOnLoad} from '@Components/poster-editor/poster-editor-reducer';
import {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import styles from './poster-add-items-panel.module.scss';
import {ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import {getHasUserSeenAiVoice} from '@Libraries/text-to-speech.library';
import {AddAiItemPopUpSubPanel} from '@Components/add-ai-item-button/components/add-ai-item-dropdown-popup-content/add-ai-item-dropdown-popup-content.types';

export enum AddItemOptions {
  DEFAULT = 'default',
  TEMPLATE = 'template',
  MEDIA = 'media',
  UPLOADS = 'uploads',
  TEXT = 'text',
  SLIDESHOW = 'slideshow',
  LAYOUT = 'layout',
  AI_IMAGE = 'ai-image',
  AI_MENU = 'AI_MENU',
  RECORD = 'record',
}

interface AddItemsPanelProps {
  panelId: string;
  panelToShowOnOpen?: AddItemOptions;
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/add-items/';
const DEFAULT_PANEL_CLOSE_THRESHOLD = 230;
const AI_IMAGE_GRID_ID = 'AI_IMAGE_GRID_BOTTOM_BAR';

function PosterAddItemsPanel({panelToShowOnOpen = AddItemOptions.DEFAULT, ...props}: AddItemsPanelProps): ReactElement<any> {
  const [showAiVoiceAsNewFeature, setShowAiVoiceAsNewFeature] = useState<boolean>(false);
  const [selectedPanel, setSelectedPanel] = useState(panelToShowOnOpen);
  const [panelModule, setPanelModule] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const {windowHeight} = useWindowSize();
  const onClose = useDefaultPanelClose(props.panelId);
  const dispatch = useAppDispatch();
  const numberOfAiImageGridItems = useAppSelector((state) => {
    return getNoOfGridItems(state, AI_IMAGE_GRID_ID);
  });
  const showAsNewFeature = useMemo((): AiOptionsSubpanels => {
    const newFeaturePills: AiOptionsSubpanels = [];
    if (showAiVoiceAsNewFeature) {
      newFeaturePills.push(AddAiItemPopUpSubPanel.AI_VOICE);
    }
    return newFeaturePills;
  }, [showAiVoiceAsNewFeature]);

  useEffect(() => {
    updateHeightInRedux(panelToShowOnOpen);
    updatePanelCloseThreshold(panelToShowOnOpen);
  }, []);

  useEffect((): void => {
    void setPanels();
  }, [selectedPanel]);

  const updatePanelCloseThreshold = (type: AddItemOptions): void => {
    dispatch(
      updatePanelProperties({
        panelId: props.panelId,
        bottomSheetCloseThreshold: type === AddItemOptions.DEFAULT ? DEFAULT_PANEL_CLOSE_THRESHOLD : undefined,
      })
    );
  };

  const updateHeightInRedux = (type: AddItemOptions): void => {
    updatePanelCloseThreshold(type);
    dispatch(
      updatePanelSizeData({
        sizeDataId: props.panelId,
        sizeData: {
          height: getHeightAccordingToMorphType(type),
        },
      })
    );
  };

  const getHeightAccordingToMorphType = (type: AddItemOptions): string => {
    switch (type) {
      case AddItemOptions.MEDIA:
      case AddItemOptions.AI_IMAGE:
      case AddItemOptions.UPLOADS:
      case AddItemOptions.TEMPLATE:
        return '1000px';
      case AddItemOptions.TEXT:
        return TEXT_OPTIONS_HEIGHT;
      case AddItemOptions.RECORD:
      case AddItemOptions.SLIDESHOW:
        return '308px';
      case AddItemOptions.AI_MENU:
        return AI_OPTIONS_HEIGHT;
      case AddItemOptions.LAYOUT:
        return '364px';
      case AddItemOptions.DEFAULT:
      default:
        if (windowHeight < 710) {
          return '600px';
        }
        return '734px';
    }
  };

  const getHeader = (): ReactElement<any> => {
    return <PanelHeader title={window.i18next.t('pmwjs_add_capitalized')} onClose={onClose} />;
  };

  const getItems = (): ControlledListItemType[] => {
    return [
      {
        id: 'bottombar-uploads-media',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_uploads'),
        className: styles.listItems,
        subText: window.i18next.t('pmwjs_add_upload_media_description'),
        onClick: (): void => {
          updateHeightInRedux(AddItemOptions.UPLOADS);
          setSelectedPanel(AddItemOptions.UPLOADS);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}uploads.${getCompatibleImageFileExtension('webp')}`),
        },
      },
      {
        id: 'bottombar-templates-option',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_templates'),
        textClasses: `capitalize-sentence ${styles.capitalise}`,
        className: `${styles.listItems} ${isTemplatesPopupHidden() ? '_hidden' : ''}`,
        subText: window.i18next.t('pmwjs_templates_description'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.TEMPLATES_MENU_OPEN);
          updateHeightInRedux(AddItemOptions.TEMPLATE);
          setSelectedPanel(AddItemOptions.TEMPLATE);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}template.${getCompatibleImageFileExtension('webp')}`),
        },
        hasPulsatingDot: showTemplatesPopupPulsatingDot(),
      },
      {
        id: 'bottombar-stock-media',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        className: styles.listItems,
        text: window.i18next.t('pmwjs_media'),
        subText: window.i18next.t('pmwjs_add_stock_media_description'),
        onClick: (): void => {
          updateHeightInRedux(AddItemOptions.MEDIA);
          setSelectedPanel(AddItemOptions.MEDIA);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}media.${getCompatibleImageFileExtension('webp')}`),
        },
      },
      {
        id: 'bottombar-plain-text',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        className: styles.listItems,
        text: window.i18next.t('pmwjs_text'),
        subText: window.i18next.t('pmwjs_add_plain_text_description'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.TEXT_MENU_OPEN);
          updateHeightInRedux(AddItemOptions.TEXT);
          setSelectedPanel(AddItemOptions.TEXT);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}text.${getCompatibleImageFileExtension('webp')}`),
        },
      },
      {
        id: 'bottom-bar-add-ai-menu',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        className: styles.listItems,
        activityIndicatorType: showAiVoiceAsNewFeature ? ActivityIndicatorType.NEW_PULSE : undefined,
        text: window.i18next.t('pmwjs_ai'),
        subText: window.i18next.t('pmwjs_ai_menu_desc'),
        textClasses: styles.aiImageText,
        onClick: (): void => {
          updateHeightInRedux(AddItemOptions.AI_MENU);
          setSelectedPanel(AddItemOptions.AI_MENU);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}ai-option.${getCompatibleImageFileExtension('webp')}`),
        },
      },
      {
        id: 'record-options',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        className: styles.listItems,
        text: window.i18next.t('pmwjs_record'),
        subText: window.i18next.t('pmwjs_record_description'),
        onClick: (): void => {
          setCookie(RECORDING_OPTIONS_COOKIE, '1', 180);
          trackPosterBuilderGA4Events(GA4EventName.RECORD_MENU_OPEN);
          updateHeightInRedux(AddItemOptions.RECORD);
          setSelectedPanel(AddItemOptions.RECORD);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}record.${getCompatibleImageFileExtension('webp')}`),
        },
        hasPulsatingDot: !readCookie(RECORDING_OPTIONS_COOKIE),
      },
      {
        id: 'bottombar-slideshow',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        className: styles.listItems,
        text: window.i18next.t('pmwjs_slideshow'),
        subText: window.i18next.t('pmwjs_add_slideshow_description'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.SLIDESHOW_MENU_OPEN);
          updateHeightInRedux(AddItemOptions.SLIDESHOW);
          setSelectedPanel(AddItemOptions.SLIDESHOW);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}slideshow.${getCompatibleImageFileExtension('webp')}`),
        },
      },
      {
        id: 'bottombar-draw',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        className: styles.listItems,
        text: window.i18next.t('pmwjs_draw'),
        subText: window.i18next.t('pmwjs_add_drawing_description'),
        onClick: (): void => {
          window.posterEditor?.whiteboard?.drawing.toggleDrawMode();
          onClose();
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}draw.${getCompatibleImageFileExtension('webp')}`),
        },
      },
      {
        id: 'bottombar-layout',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        className: styles.listItems,
        text: window.i18next.t('pmwjs_layout'),
        subText: window.i18next.t('pmwjs_add_layout_description'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.LAYOUT_MENU_OPEN);
          updateHeightInRedux(AddItemOptions.LAYOUT);
          setSelectedPanel(AddItemOptions.LAYOUT);
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}layout.${getCompatibleImageFileExtension('webp')}`),
        },
      },
      {
        id: 'bottombar-qr-code',
        className: styles.listItems,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_qr_code'),
        subText: window.i18next.t('pmwjs_add_qrcode_description_mobile'),
        onClick: (): void => {
          trackPosterBuilderGA4Events(GA4EventName.QR_CODE_ADDED);
          addQrCodeToPage();
          onClose();
        },
        thumbnail: {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}qrcode.${getCompatibleImageFileExtension('webp')}`),
        },
      },
    ];
  };

  const getContent = (): ReactElement<any> => {
    return (
      <PanelContent className={`spacing-p-l-4 spacing-p-r-4 spacing-p-t-1 spacing-p-b-4 ${styles.container}`}>
        <ControlledList className="spacing-m-0" items={getItems()} />
      </PanelContent>
    );
  };

  const setPanels = async (): Promise<void> => {
    let module: any;
    setLoading(true);
    switch (selectedPanel) {
      case AddItemOptions.TEXT:
        module = await import('./components/text-options/text-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
            onDone={() => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
              onClose();
            }}
            onSelectAiTextArt={() => {
              dispatch(updateFeatureToShowOnLoad(FeatureToShowOnLoad.AI_TEXT_ART));
              updateHeightInRedux(AddItemOptions.AI_MENU);
              setSelectedPanel(AddItemOptions.AI_MENU);
            }}
          />
        );
        break;
      case AddItemOptions.TEMPLATE:
        module = await import('./components/templates-panel/templates-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.AI_IMAGE:
        module = await import('@Panels/ai-image-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
            shouldLoadDataUrlImage
            onAddSelected={onAddItems}
            gridID={AI_IMAGE_GRID_ID}
            className={`spacing-p-4 ${numberOfAiImageGridItems > 0 ? styles.fullAIGridContent : styles.emptyAIGridContent}`}
          />
        );
        break;
      case AddItemOptions.AI_MENU:
        module = await import('./components/ai-options/ai-options');
        setPanelModule(
          <module.default
            onDone={() => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
              onClose();
            }}
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
            showNewAiFeaturePulse={showAsNewFeature}
          />
        );
        break;
      case AddItemOptions.LAYOUT:
        module = await import('./components/layout-options/layout-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.SLIDESHOW:
        module = await import('./components/slide-show-options/slide-show-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.MEDIA:
        module = await import('@Panels/stock-panel');
        setPanelModule(
          <module.default
            showTextToImageTool
            panelId={props.panelId}
            defaultTab={UploadsMediaType.IMAGE}
            shouldLoadDataUrlImage
            onAddSelected={onAddItems}
            onAddBrandAsset={(): void => {
              void onAddBrandAssetClickedHandler();
            }}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.UPLOADS:
        module = await import('@Panels/user-media-panel');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            defaultTab={UploadsMediaType.IMAGE}
            shouldLoadDataUrlImage
            onAddSelected={onAddItems}
            onAddBrandAsset={(): void => {
              void onAddBrandAssetClickedHandler();
            }}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.RECORD:
        module = await import('./components/record-options/record-options');
        setPanelModule(
          <module.default
            panelId={props.panelId}
            onClose={onClose}
            onBack={(): void => {
              updateHeightInRedux(AddItemOptions.DEFAULT);
              setSelectedPanel(AddItemOptions.DEFAULT);
            }}
          />
        );
        break;
      case AddItemOptions.DEFAULT:
      default:
        setPanelModule(<Panel panelHeader={getHeader()} panelContent={getContent()} />);
    }
    setLoading(false);
  };

  const initAiVoiceNewStatus = async (): Promise<void> => {
    try {
      const response = await getHasUserSeenAiVoice();
      setShowAiVoiceAsNewFeature(!response);
    } catch (e) {
      setShowAiVoiceAsNewFeature(false);
    }
  };

  const init = (): void => {
    void initAiVoiceNewStatus();
  };

  useEffect(() => {
    init();
  }, []);

  if (!panelModule || loading) {
    return <AddItemPanelLoading />;
  }

  return panelModule as ReactElement<any>;
}

export default PosterAddItemsPanel;
